import usePairedDevice from "@/hooks/usePairedDevice";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import { WSMRemoteCfgFeatures } from "@WahooFitness/wsm-native/dist/esm/types/remote_config_cfg_status";
import useSWR from "swr";

// The row needs the plugin to implement the bell methods. Older wsm-native builds in the field
// do not, and Capacitor rejects a call to a missing method with UNIMPLEMENTED.
function useBikeBellAvailability(appToken: string) {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { device, isDeviceConnected } = usePairedDevice(appToken);
  const { enabled: flagEnabled, featuresAreLoading } = useSettingsVersionedFeatures(
    "ELEMNT_BIKE_BELL_MINPROFILE_VERSION"
  );

  const canReadCapability = flagEnabled && device?.sensorId !== undefined && isDeviceConnected;

  const { data: deviceSupported, isLoading: deviceSupportedIsLoading } = useSWR(
    canReadCapability && ["bikeBellCfgFeature", device.sensorId],
    async ([_key, sensorId]) => {
      const features: WSMRemoteCfgFeatures | undefined = (
        await sensorManagerPlugin.getRemoteCfgStatus({ sensorId })
      )?.result?.features;
      if (!features?.bike_bell_cfg) return false;
      try {
        await sensorManagerPlugin.bcGetBikeBellEnabled({ appToken });
        return true;
      } catch (error) {
        if (
          typeof error === "object" &&
          error !== null &&
          (error as { code?: string }).code === "UNIMPLEMENTED"
        ) {
          return false;
        }
        throw error;
      }
    },
    // The device object changes with every rssi/battery/firmware update, so the key is its id.
    { fallbackData: false, keepPreviousData: true }
  );

  return {
    // keepPreviousData hands back the last answer even once the read is closed, so the gate hides it.
    available: canReadCapability && deviceSupported,
    isLoading: featuresAreLoading || deviceSupportedIsLoading,
  };
}

export { useBikeBellAvailability };
